import { Alert, Button, Space, Spin, Typography } from 'antd';
import { Column } from '@ydistri/ds';
import { formatNumber } from '@ydistri/generic-utils';
import React, { useEffect, useState } from 'react';
import ScopeEditorController from '../../../components/domain/Scopes/ScopeEditorController';
import { useStableParams } from '../../../routes/useStableParams';
import {
  SharedScopeNotice,
  SharedScopeNoticeHeader,
  SharedScopeNoticeMessage,
  SharedScopeUsageList,
} from './AsoScopeAssignmentStyledComponents';
import AsoScopeSelector from './AsoScopeSelector';
import type { AsoScopeAssignmentLabel } from './asoScopeAssignmentTypes';
import { useAsoScopeAssignment } from './hooks/useAsoScopeAssignment';

export interface AsoScopeAssignmentRenderState {
  content: React.ReactNode;
  selector: React.ReactNode;
}

interface AsoScopeAssignmentProps {
  asoScenarioId: number;
  assignmentLabel: AsoScopeAssignmentLabel;
  children: (state: AsoScopeAssignmentRenderState) => React.ReactNode;
  isAssigning: boolean;
  onAssignScope: (scopeId: number | null) => Promise<void>;
  isAssignmentDisabled?: boolean;
  selectedScopeId?: number | null;
}

interface ResolvedSkuCountProps {
  count?: number;
  isError: boolean;
  isLoading: boolean;
}

interface ScopeUsagesProps {
  isError: boolean;
  isLoading: boolean;
  usages: Array<{ key: string; label: string }>;
}

const ScopeUsages: React.FC<ScopeUsagesProps> = ({ isError, isLoading, usages }) => {
  if (isLoading) {
    return (
      <Space size="small">
        <Spin size="small" />
        <Typography.Text type="secondary">Loading Scope usages…</Typography.Text>
      </Space>
    );
  }

  if (isError) {
    return <Typography.Text type="warning">Scope usages could not be loaded.</Typography.Text>;
  }

  if (usages.length === 0) {
    return <Typography.Text type="secondary">No usages found.</Typography.Text>;
  }

  return (
    <SharedScopeUsageList aria-label="Scope usages">
      {usages.map(usage => (
        <li key={usage.key}>{usage.label}</li>
      ))}
    </SharedScopeUsageList>
  );
};

const ResolvedSkuCount: React.FC<ResolvedSkuCountProps> = ({ count, isError, isLoading }) => {
  if (isLoading) {
    return (
      <Space size="small">
        <Spin size="small" />
        <Typography.Text type="secondary">Resolving SKUs…</Typography.Text>
      </Space>
    );
  }

  if (isError) {
    return <Typography.Text type="warning">Resolved SKU count unavailable</Typography.Text>;
  }

  const resolvedSkuCount = count ?? 0;

  return (
    <Typography.Text type={resolvedSkuCount === 0 ? 'warning' : 'secondary'}>
      {formatNumber(resolvedSkuCount)} resolved SKUs
    </Typography.Text>
  );
};

const AsoScopeAssignment: React.FC<AsoScopeAssignmentProps> = ({
  asoScenarioId,
  assignmentLabel,
  children,
  isAssigning,
  onAssignScope,
  isAssignmentDisabled = false,
  selectedScopeId,
}) => {
  const { projectShortName } = useStableParams();
  const [areScopeUsagesShown, setAreScopeUsagesShown] = useState(false);
  const assignment = useAsoScopeAssignment({
    asoScenarioId,
    assignmentLabel,
    isAssigning,
    onAssignScope,
    selectedScopeId,
  });

  useEffect(() => {
    setAreScopeUsagesShown(false);
  }, [assignment.displayedScopeId]);

  const controlsDisabled = assignment.isLocked || isAssignmentDisabled;
  const isScopeControlSaving = assignment.isCreatingOrAssigning || assignment.isUpdatingSharing;

  const selector = (
    <AsoScopeSelector
      disabled={controlsDisabled}
      displayedScopeId={assignment.displayedScopeId}
      hasPendingCreatedScope={Boolean(assignment.createdScope)}
      hasSelectedScope={assignment.hasSelectedScope}
      hasSelectedScopeSharingState={assignment.hasSelectedScopeSharingState}
      hasZeroResolvedSkus={assignment.hasZeroResolvedSkus}
      isFetching={assignment.isFetchingScopes}
      isSaving={isScopeControlSaving}
      isSelectedScopeShared={assignment.isSelectedScopeShared}
      isSharingToggleDisabled={!assignment.canToggleSelectedScopeSharing}
      isUpdatingSharing={assignment.isUpdatingSharing}
      options={assignment.scopeOptions}
      onChange={value => void assignment.changeSelection(value)}
      onCreate={() => void assignment.createAndAssignScope()}
      onToggleSharing={() => void assignment.toggleSelectedScopeSharing()}
    />
  );

  const content = (
    <Column $gap="0.75rem">
      {assignment.isScopesError && (
        <Alert message="Reusable Scopes could not be loaded." showIcon type="error" />
      )}
      {assignment.hasSelectedScope && assignment.isSelectedScopeError && (
        <Alert message="The selected reusable Scope could not be loaded." showIcon type="error" />
      )}
      {assignment.hasSelectedScope && assignment.isSkuCountError && (
        <Alert
          message="The resolved SKU count could not be loaded. ASO will validate it before computing."
          showIcon
          type="warning"
        />
      )}
      {assignment.assignmentError && (
        <Alert message={assignment.assignmentError} showIcon type="error" />
      )}
      {assignment.creationError && (
        <Alert message={assignment.creationError} showIcon type="error" />
      )}
      {assignment.sharingError && <Alert message={assignment.sharingError} showIcon type="error" />}
      {assignment.selectedScope?.isArchived && (
        <Alert
          message="This archived Scope remains linked for reference, but cannot be selected again or edited."
          showIcon
          type="warning"
        />
      )}
      {assignment.isSelectedScopeShared && assignment.hasExternalScopeUsages && (
        <SharedScopeNotice>
          <SharedScopeNoticeHeader>
            <SharedScopeNoticeMessage>
              <Typography.Text strong>
                This scope is already used, changing it will affect other places
              </Typography.Text>
            </SharedScopeNoticeMessage>
            <Button
              size="small"
              type="link"
              onClick={() => setAreScopeUsagesShown(value => !value)}
            >
              {areScopeUsagesShown ? 'Hide usages' : 'Show usages'}
            </Button>
            <Button
              disabled={
                controlsDisabled || assignment.isCreatingOrAssigning || !assignment.selectedScope
              }
              loading={assignment.isCreatingOrAssigning}
              type="primary"
              onClick={() => void assignment.makeScopeCopy()}
            >
              {assignment.createdScope ? 'Retry assignment' : 'Make local copy'}
            </Button>
          </SharedScopeNoticeHeader>
          {areScopeUsagesShown && (
            <ScopeUsages
              isError={assignment.isScopeUsagesError}
              isLoading={assignment.isFetchingScopeUsages}
              usages={assignment.selectedScopeUsages}
            />
          )}
        </SharedScopeNotice>
      )}
      {assignment.displayedScopeId !== null &&
        assignment.displayedScopeId !== undefined &&
        projectShortName && (
          <ScopeEditorController
            embedded
            footerContent={
              <ResolvedSkuCount
                count={assignment.resolvedSkuCount}
                isError={assignment.isSkuCountError}
                isLoading={assignment.isFetchingSkuCount}
              />
            }
            projectShortName={projectShortName}
            readOnly={assignment.isReadOnly}
            scopeId={assignment.displayedScopeId}
          />
        )}
    </Column>
  );

  return <>{children({ content, selector })}</>;
};

export default AsoScopeAssignment;
